import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import "./legalPage.css";
import LegalBg from '../../assets/legal-bg.png';

type LegalType = "privacy-policy" | "terms-of-service" | "cookie-policy";

const CONTENT: Record<
  LegalType,
  {
    title: string;
    subtitle: string;
    sections: { heading: string; content: string[] }[];
  }
> = {
  "privacy-policy": {
    title: "Privacy Policy",
    subtitle: "Your data, privacy, and trust matter to us.",
    sections: [
      {
        heading: "Information We Collect",
        content: [
          "Personal identification information such as name, email, and contact details.",
          "Technical data including IP address, browser type, and usage patterns.",
        ],
      },
      {
        heading: "How We Use Your Data",
        content: [
          "To provide and improve our services.",
          "To communicate important updates and respond to inquiries.",
          "To ensure security and prevent fraudulent activity.",
        ],
      },
      {
        heading: "Data Protection",
        content: [
          "We implement industry-standard security measures.",
          "Access to data is restricted to authorized personnel only.",
        ],
      },
    ],
  },

  "terms-of-service": {
    title: "Terms of Service",
    subtitle: "Clear rules for using our services responsibly.",
    sections: [
      {
        heading: "Acceptance of Terms",
        content: [
          "By accessing our website, you agree to these terms.",
          "If you do not agree, please discontinue use.",
        ],
      },
      {
        heading: "Use of Services",
        content: [
          "Services must not be used for unlawful activities.",
          "You agree not to attempt unauthorized access.",
        ],
      },
      {
        heading: "Limitation of Liability",
        content: [
          "Nordic Integration Works is not liable for indirect damages.",
          "Service availability is not guaranteed at all times.",
        ],
      },
    ],
  },

  "cookie-policy": {
    title: "Cookie Policy",
    subtitle: "How we use cookies to enhance your experience.",
    sections: [
      {
        heading: "What Are Cookies?",
        content: [
          "Cookies are small text files stored on your device.",
          "They help us improve usability and performance.",
        ],
      },
      {
        heading: "Types of Cookies We Use",
        content: [
          "Essential cookies for site functionality.",
          "Analytics cookies to understand usage patterns.",
        ],
      },
      {
        heading: "Managing Cookies",
        content: [
          "You can control cookies through browser settings.",
          "Disabling cookies may affect site functionality.",
        ],
      },
    ],
  },
};

export const LegalPage: React.FC = () => {
  const { type } = useParams<{ type: LegalType }>();
  const navigate = useNavigate();

  if (!type || !CONTENT[type]) {
    return (
      <div className="legal__notfound">
        <h2>Page not found</h2>
      </div>
    );
  }

  const data = CONTENT[type];

  return (
    <div className="legal">
      {/* HERO */}
      <motion.section
        className="legal__hero"
style={{
    backgroundImage: `url(${LegalBg})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  }}        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="legal__overlay" />
        <motion.div
          className="legal__hero-content"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {/* <h1>{data.title}</h1> */}
          {/* <p>{data.subtitle}</p> */}
        </motion.div>
      </motion.section>

      {/* CONTENT */}
      <section className="legal__content">
        {data.sections.map((section, idx) => (
          <motion.div
            key={idx}
            className="legal__card"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: idx * 0.1 }}
          >
            <h3>{section.heading}</h3>
            <ul>
              {section.content.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </motion.div>
        ))}
      </section>

      {/* FOOTER NAV */}
      <motion.div
        className="legal__nav"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <button onClick={() => navigate("/legal/privacy-policy")}>
          Privacy Policy
        </button>
        <button onClick={() => navigate("/legal/terms-of-service")}>
          Terms of Service
        </button>
        <button onClick={() => navigate("/legal/cookie-policy")}>
          Cookie Policy
        </button>
      </motion.div>
    </div>
  );
};
